import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import api from '../api';
import LiveBoard from './Board';
import KanbanBoard from '../components/KanbanBoard';

// ─── Shared input styles (16px prevents iOS zoom) ────────────────────────────
const inp = {
  width: '100%', background: '#161b22', border: '1px solid #30363d',
  borderRadius: 8, color: '#e6edf3', padding: '16px', fontSize: 16,
  fontFamily: 'IBM Plex Sans, sans-serif', lineHeight: 1.4,
  boxSizing: 'border-box', WebkitAppearance: 'none', appearance: 'none',
};
const inpFocus = { borderColor: '#f0a500', outline: 'none' };

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#484f58', marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}
function Sel({ value, onChange, children }) {
  const [f, setF] = useState(false);
  return (
    <select value={value} onChange={onChange} onFocus={() => setF(true)} onBlur={() => setF(false)}
      style={{ ...inp, ...(f ? inpFocus : {}), cursor: 'pointer' }}>{children}</select>
  );
}
function Inp({ value, onChange, type = 'text', ...rest }) {
  const [f, setF] = useState(false);
  return (
    <input type={type} value={value} onChange={onChange}
      onFocus={() => setF(true)} onBlur={() => setF(false)}
      style={{ ...inp, ...(f ? inpFocus : {}) }} {...rest} />
  );
}
function Toggle({ label, sub, value, onChange }) {
  return (
    <div onClick={() => onChange(!value)} style={{
      display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', marginBottom: 16,
      background: value ? 'rgba(88,166,255,0.08)' : '#1c2333',
      border: value ? '1px solid rgba(88,166,255,0.35)' : '1px solid #30363d',
      borderRadius: 10, padding: '16px',
    }}>
      <div style={{ width: 48, height: 26, borderRadius: 13, flexShrink: 0, background: value ? '#58a6ff' : '#30363d', position: 'relative', transition: 'background 0.2s' }}>
        <div style={{ position: 'absolute', top: 3, left: value ? 25 : 3, width: 20, height: 20, borderRadius: '50%', background: value ? '#fff' : '#8b949e', transition: 'left 0.2s' }} />
      </div>
      <div>
        <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 15, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: value ? '#58a6ff' : '#e6edf3' }}>{label}</div>
        {sub && <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#484f58', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}
function Toast({ msg, type, onDismiss }) {
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [msg, onDismiss]);
  if (!msg) return null;
  const ok = type === 'success';
  return (
    <div style={{
      position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)',
      zIndex: 3000, maxWidth: 400, width: 'calc(100% - 32px)',
      background: ok ? 'rgba(63,185,80,0.15)' : 'rgba(248,81,73,0.15)',
      border: `1px solid ${ok ? 'rgba(63,185,80,0.4)' : 'rgba(248,81,73,0.4)'}`,
      borderRadius: 10, padding: '14px 18px', color: ok ? '#3fb950' : '#f85149',
      fontFamily: 'IBM Plex Mono, monospace', fontSize: 13,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
    }}>
      <span>{ok ? '✓  ' : '✗  '}{msg}</span>
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
    </div>
  );
}

function elapsed(startedAt, now) {
  const ms = now - new Date(startedAt).getTime();
  const m = Math.floor(ms / 60000);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
}

// ─── Clock On/Off using DB sessions ──────────────────────────────────────────
function ClockForm({ projects, workCentres, staff, onLogged }) {
  const [form, setForm]   = useState({ staff_id: '', work_centre_id: '', project_id: '', non_productive: false, description: '' });
  const [sessions, setSessions] = useState([]);
  const [now, setNow]     = useState(Date.now());
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const loadSessions = useCallback(() => {
    api.get('/clock-sessions').then(r => setSessions(r.data.value || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadSessions();
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(tick);
  }, [loadSessions]);

  const clockOn = async () => {
    if (!form.staff_id || !form.work_centre_id) {
      setToast({ msg: 'Select your name and work centre.', type: 'error' }); return;
    }
    if (!form.non_productive && !form.project_id) {
      setToast({ msg: 'Select a project, or enable Non-Productive.', type: 'error' }); return;
    }
    try {
      const resp = await api.post('/clock-sessions', {
        staff_id:       parseInt(form.staff_id),
        work_centre_id: parseInt(form.work_centre_id),
        project_id:     form.non_productive ? null : parseInt(form.project_id),
        non_productive: form.non_productive,
        description:    form.description || null,
      });
      setSessions(prev => [...prev, resp.data]);
      const time = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      setToast({ msg: `${resp.data.staff_name} clocked on at ${time}`, type: 'success' });
      set('project_id', ''); set('description', '');
    } catch (e) {
      setToast({ msg: e.response?.data?.error || e.message, type: 'error' });
    }
  };

  const clockOff = async (session) => {
    setSaving(session.id);
    try {
      const resp = await api.delete(`/clock-sessions/${session.id}`);
      setSessions(prev => prev.filter(s => s.id !== session.id));
      setToast({ msg: `${resp.data.staff_name} clocked off — ${resp.data.hours_logged.toFixed(2)}h logged`, type: 'success' });
      onLogged();
    } catch (e) {
      setToast({ msg: e.response?.data?.error || e.message, type: 'error' });
    }
    setSaving(null);
  };

  const selProject = !form.non_productive && form.project_id
    ? projects.find(p => p.id === parseInt(form.project_id)) : null;

  return (
    <div>
      <Toast msg={toast?.msg} type={toast?.type} onDismiss={() => setToast(null)} />
      <Toggle label="Non-Productive Time" sub="Clean down, shop work, training, etc."
        value={form.non_productive} onChange={v => { set('non_productive', v); set('project_id', ''); }} />
      <Field label="Who are you? *">
        <Sel value={form.staff_id} onChange={e => set('staff_id', e.target.value)}>
          <option value="">Select name...</option>
          {staff.map(s => <option key={s.id} value={s.id}>{s.name}{s.role ? ` — ${s.role}` : ''}</option>)}
        </Sel>
      </Field>
      <Field label="Work Centre *">
        <Sel value={form.work_centre_id} onChange={e => set('work_centre_id', e.target.value)}>
          <option value="">Select work centre...</option>
          {workCentres.map(wc => <option key={wc.id} value={wc.id}>{wc.name}</option>)}
        </Sel>
      </Field>
      {form.non_productive ? (
        <Field label="What are you doing?">
          <Inp value={form.description} onChange={e => set('description', e.target.value)}
            placeholder="e.g. Clean down, general shop work..." />
        </Field>
      ) : (
        <>
          <Field label="Project *">
            <Sel value={form.project_id} onChange={e => set('project_id', e.target.value)}>
              <option value="">Select project...</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
            </Sel>
          </Field>
          {selProject && (
            <div style={{ background: '#0d1117', border: '1px solid #f0a500', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
              <span style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#f0a500', fontSize: 15, fontWeight: 700 }}>{selProject.job_number}</span>
              <span style={{ fontSize: 13, color: '#8b949e', marginLeft: 10 }}>{selProject.customer_name}</span>
            </div>
          )}
        </>
      )}
      <button onClick={clockOn} style={{
        width: '100%', padding: '20px', border: 'none', borderRadius: 10, marginBottom: 28,
        background: '#3fb950', color: '#fff',
        fontFamily: 'Rajdhani, sans-serif', fontSize: 22, fontWeight: 700,
        letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer',
      }}>Clock On</button>

      {sessions.length > 0 && (
        <>
          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#484f58', marginBottom: 12 }}>
            Clocked On ({sessions.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sessions.map(s => (
              <div key={s.id} style={{
                background: '#1c2333', border: '1px solid #30363d', borderRadius: 10, padding: '16px',
                display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{s.staff_name}</div>
                  {s.non_productive ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ background: 'rgba(88,166,255,0.12)', color: '#58a6ff', border: '1px solid rgba(88,166,255,0.3)', borderRadius: 3, padding: '1px 7px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase' }}>NON-PROD</span>
                      {s.description && <span style={{ fontSize: 13, color: '#8b949e' }}>{s.description}</span>}
                    </div>
                  ) : (
                    <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, color: '#f0a500', marginBottom: 4 }}>
                      {s.job_number} <span style={{ color: '#8b949e', fontFamily: 'IBM Plex Sans, sans-serif' }}>— {s.project_title}</span>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: '#484f58' }}>{s.work_centre_name} · {elapsed(s.started_at, now)} ago</div>
                </div>
                <button onClick={() => clockOff(s)} disabled={saving === s.id} style={{
                  padding: '14px 16px', border: 'none', borderRadius: 8, cursor: 'pointer',
                  background: saving === s.id ? '#1c2333' : '#f85149', color: '#fff',
                  fontFamily: 'Rajdhani, sans-serif', fontSize: 15, fontWeight: 700,
                  letterSpacing: 1, textTransform: 'uppercase', minWidth: 88,
                  opacity: saving === s.id ? 0.6 : 1,
                }}>
                  {saving === s.id ? '...' : 'Clock Off'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Manual entry (collapsible) ───────────────────────────────────────────────
function ManualForm({ projects, workCentres, staff, onLogged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    project_id: '', work_centre_id: '', staff_id: '',
    log_date: new Date().toISOString().slice(0, 10),
    hours_decimal: '', non_productive: false, notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast]   = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.work_centre_id || !form.staff_id || !form.hours_decimal) {
      setToast({ msg: 'Work centre, staff and hours required.', type: 'error' }); return;
    }
    if (!form.non_productive && !form.project_id) {
      setToast({ msg: 'Select a project or enable Non-Productive.', type: 'error' }); return;
    }
    setSaving(true);
    try {
      await api.post('/production-logs', {
        project_id:     form.non_productive ? null : parseInt(form.project_id),
        work_centre_id: parseInt(form.work_centre_id),
        staff_id:       parseInt(form.staff_id),
        log_date:       form.log_date,
        hours_decimal:  parseFloat(form.hours_decimal),
        non_productive: form.non_productive,
        notes:          form.notes || null,
      });
      setToast({ msg: 'Entry logged.', type: 'success' });
      setForm(p => ({ ...p, hours_decimal: '', notes: '', project_id: '' }));
      onLogged();
    } catch (e) {
      setToast({ msg: e.response?.data?.error || e.message, type: 'error' });
    }
    setSaving(false);
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Toast msg={toast?.msg} type={toast?.type} onDismiss={() => setToast(null)} />
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', padding: '14px', border: '1px solid #30363d', borderRadius: 8,
        background: '#1c2333', color: '#8b949e',
        fontFamily: 'Rajdhani, sans-serif', fontSize: 15, fontWeight: 600,
        letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span>Manual / Retrospective Entry</span>
        <span style={{ fontSize: 18 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ background: '#1c2333', border: '1px solid #30363d', borderRadius: '0 0 8px 8px', padding: '20px', marginTop: -1 }}>
          <Toggle label="Non-Productive" sub="Not linked to a project"
            value={form.non_productive} onChange={v => { set('non_productive', v); set('project_id', ''); }} />
          {!form.non_productive && (
            <Field label="Project *">
              <Sel value={form.project_id} onChange={e => set('project_id', e.target.value)}>
                <option value="">Select project...</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
              </Sel>
            </Field>
          )}
          <Field label="Work Centre *">
            <Sel value={form.work_centre_id} onChange={e => set('work_centre_id', e.target.value)}>
              <option value="">Select work centre...</option>
              {workCentres.map(wc => <option key={wc.id} value={wc.id}>{wc.name}</option>)}
            </Sel>
          </Field>
          <Field label="Staff Member *">
            <Sel value={form.staff_id} onChange={e => set('staff_id', e.target.value)}>
              <option value="">Select name...</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Sel>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Date *"><Inp type="date" value={form.log_date} onChange={e => set('log_date', e.target.value)} /></Field>
            <Field label="Hours *"><Inp type="number" value={form.hours_decimal} onChange={e => set('hours_decimal', e.target.value)} placeholder="e.g. 2.5" min="0.25" max="24" step="0.25" /></Field>
          </div>
          <Field label="Notes">
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Any notes..." style={{ ...inp, resize: 'vertical', minHeight: 72 }} />
          </Field>
          <button onClick={submit} disabled={saving} style={{
            width: '100%', padding: '16px', border: 'none', borderRadius: 8,
            background: saving ? '#b87d00' : '#f0a500', color: '#000',
            fontFamily: 'Rajdhani, sans-serif', fontSize: 18, fontWeight: 700,
            letterSpacing: 2, textTransform: 'uppercase', cursor: saving ? 'not-allowed' : 'pointer',
          }}>
            {saving ? 'Saving...' : '⚙ Log Entry'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Today's summary strip ────────────────────────────────────────────────────
function TodaySummary({ logs }) {
  if (!logs.length) return null;
  const total = logs.reduce((s, l) => s + parseFloat(l.hours_decimal || 0), 0);
  const byWc  = logs.reduce((acc, l) => { acc[l.work_centre_name] = (acc[l.work_centre_name] || 0) + parseFloat(l.hours_decimal || 0); return acc; }, {});
  return (
    <div style={{ background: '#1c2333', border: '1px solid #f0a500', borderRadius: 10, padding: '14px 16px', marginBottom: 20, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
      <div>
        <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, color: '#484f58', letterSpacing: 2, textTransform: 'uppercase' }}>Today</div>
        <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 32, fontWeight: 700, color: '#f0a500', lineHeight: 1 }}>
          {total.toFixed(2)}<span style={{ fontSize: 13, color: '#484f58', marginLeft: 4 }}>h</span>
        </div>
        <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#484f58', marginTop: 2 }}>{logs.length} entries</div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
        {Object.entries(byWc).map(([wc, h]) => (
          <div key={wc} style={{ background: '#161b22', borderRadius: 8, padding: '6px 12px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, color: '#484f58', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>{wc}</div>
            <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 16, fontWeight: 700, color: '#e6edf3' }}>{h.toFixed(2)}h</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Production page ──────────────────────────────────────────────────────────
function ShopFloorProduction() {
  const [projects,    setProjects]    = useState([]);
  const [workCentres, setWorkCentres] = useState([]);
  const [staff,       setStaff]       = useState([]);
  const [todayLogs,   setTodayLogs]   = useState([]);

  const loadMeta = useCallback(() => {
    api.get('/projects').then(r => setProjects((r.data.value || []).filter(p => !['delivered','invoiced'].includes(p.status))));
    api.get('/work-centres').then(r => setWorkCentres((r.data.value || []).filter(w => w.is_active)));
    api.get('/staff').then(r => setStaff(r.data.value || []));
  }, []);
  const loadToday = useCallback(() => {
    api.get('/production-logs/today').then(r => setTodayLogs(r.data.value || []));
  }, []);

  useEffect(() => { loadMeta(); loadToday(); }, [loadMeta, loadToday]);

  return (
    <div>
      <TodaySummary logs={todayLogs} />
      <ClockForm  projects={projects} workCentres={workCentres} staff={staff} onLogged={loadToday} />
      <ManualForm projects={projects} workCentres={workCentres} staff={staff} onLogged={loadToday} />
    </div>
  );
}

// ─── Board page (management can force-off) ────────────────────────────────────
function ShopFloorBoard() {
  return <LiveBoard showForceOff={false} />;
}

// ─── Jobs page (read-only Kanban) ─────────────────────────────────────────────
function ShopFloorJobs() {
  return <KanbanBoard readOnly={true} />;
}

// ─── Inventory page ───────────────────────────────────────────────────────────
function ShopFloorInventory() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [catFilter, setCatFilter] = useState('');

  useEffect(() => {
    api.get('/inventory')
      .then(r => { setItems(Array.isArray(r.data) ? r.data : (r.data.value || [])); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const categories = [...new Set(items.map(i => i.category).filter(Boolean))].sort();
  const filtered   = items.filter(i => {
    const s = search.toLowerCase();
    return (!s || i.product_name?.toLowerCase().includes(s) || i.category?.toLowerCase().includes(s))
        && (!catFilter || i.category === catFilter);
  });
  const isLow = (i) => i.minimum_stock != null && parseFloat(i.quantity_on_hand) <= parseFloat(i.minimum_stock);

  if (loading) return <div style={{ textAlign: 'center', padding: '60px 0', color: '#484f58', fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, letterSpacing: 2 }}>Loading inventory…</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..."
          style={{ ...inp, flex: 1, minWidth: 180, padding: '12px 16px' }} />
        {categories.length > 0 && (
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
            style={{ ...inp, width: 'auto', padding: '12px 16px', minWidth: 140 }}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#484f58', fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, letterSpacing: 2 }}>
          {items.length === 0 ? 'No inventory items' : 'No items match your search'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(item => {
            const low = isLow(item);
            return (
              <div key={item.id} style={{
                background: '#1c2333', border: `1px solid ${low ? 'rgba(248,81,73,0.4)' : '#30363d'}`,
                borderRadius: 10, padding: '16px',
                display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 17, fontWeight: 700, marginBottom: 4 }}>{item.product_name}</div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {item.category && <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#484f58', letterSpacing: 1, textTransform: 'uppercase' }}>{item.category}</span>}
                    {item.location && <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#484f58' }}>📍 {item.location}</span>}
                    {item.minimum_stock != null && <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#484f58' }}>Min: {item.minimum_stock}</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 28, fontWeight: 700, color: low ? '#f85149' : '#e6edf3' }}>
                    {parseFloat(item.quantity_on_hand).toFixed(item.quantity_on_hand % 1 === 0 ? 0 : 1)}
                  </div>
                  {low
                    ? <span style={{ background: 'rgba(248,81,73,0.15)', color: '#f85149', border: '1px solid rgba(248,81,73,0.3)', borderRadius: 4, padding: '2px 8px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>Low Stock</span>
                    : <span style={{ background: 'rgba(63,185,80,0.1)', color: '#3fb950', border: '1px solid rgba(63,185,80,0.25)', borderRadius: 4, padding: '2px 8px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>OK</span>
                  }
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ marginTop: 12, fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#484f58', textAlign: 'right' }}>
        {filtered.length} items · {filtered.filter(isLow).length} low stock
      </div>
    </div>
  );
}

// ─── Bottom nav ───────────────────────────────────────────────────────────────
function ShopFloorNav() {
  const navStyle = (isActive) => ({
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: 4, padding: '10px 0', textDecoration: 'none', transition: 'color 0.15s',
    color: isActive ? '#f0a500' : '#484f58',
    fontFamily: 'Rajdhani, sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
  });
  const divider = <div style={{ width: 1, background: '#30363d', margin: '10px 0' }} />;
  return (
    <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: 64, background: '#161b22', borderTop: '1px solid #30363d', display: 'flex', alignItems: 'stretch', paddingBottom: 'env(safe-area-inset-bottom)', zIndex: 200 }}>
      <NavLink to="/shopfloor"           end style={({ isActive }) => navStyle(isActive)}><span style={{ fontSize: 22 }}>⚙</span><span>Production</span></NavLink>
      {divider}
      <NavLink to="/shopfloor/jobs"          style={({ isActive }) => navStyle(isActive)}><span style={{ fontSize: 22 }}>⬡</span><span>Jobs</span></NavLink>
      {divider}
      <NavLink to="/shopfloor/board"         style={({ isActive }) => navStyle(isActive)}><span style={{ fontSize: 22 }}>◉</span><span>Board</span></NavLink>
      {divider}
      <NavLink to="/shopfloor/inventory"     style={({ isActive }) => navStyle(isActive)}><span style={{ fontSize: 22 }}>▣</span><span>Inventory</span></NavLink>
    </nav>
  );
}

function LiveClock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setT(new Date()), 30000); return () => clearInterval(id); }, []);
  return <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, color: '#8b949e' }}>
    {t.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })} · {t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
  </span>;
}

// ─── Main ShopFloor shell ─────────────────────────────────────────────────────
function ShopFloorInner() {
  return (
    <div style={{ background: '#0d1117', minHeight: '100vh', color: '#e6edf3' }}>
      <header style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 60, zIndex: 200, background: '#161b22', borderBottom: '1px solid #30363d', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 20, fontWeight: 700, color: '#f0a500', letterSpacing: 2, textTransform: 'uppercase' }}>CGS</span>
          <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#484f58', letterSpacing: 3, textTransform: 'uppercase', paddingTop: 2 }}>Shop Floor</span>
        </div>
        <LiveClock />
      </header>
      <div style={{ paddingTop: 76, paddingBottom: 80, paddingLeft: 16, paddingRight: 16, maxWidth: 600, margin: '0 auto' }}>
        <Routes>
          <Route path="/"          element={<ShopFloorProduction />} />
          <Route path="jobs"       element={<ShopFloorJobs />} />
          <Route path="board"      element={<ShopFloorBoard />} />
          <Route path="inventory"  element={<ShopFloorInventory />} />
        </Routes>
      </div>
      <ShopFloorNav />
    </div>
  );
}

export default function ShopFloor() {
  return <ShopFloorInner />;
}
